import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MetricCard, KPIStrip } from '@/components/common';
import { tmsAnalyticsApi, KPIQueryParams } from '@/api/services/analytics.api';
import { TrendingUp, Clock, AlertTriangle, DollarSign, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format, subDays } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { toast } from 'sonner';

interface KPIDashboardProps {
  className?: string;
}

/**
 * Maps the analytics API's signed trend number onto MetricCard's trend shape.
 * `lowerIsBetter` flips the arrow for metrics like transit time and exceptions,
 * where a decrease is an improvement.
 */
function toTrend(value: number | null | undefined, unit = '', lowerIsBetter = false) {
  // Null means there was no prior period to compare against, so there is no
  // trend to draw — distinct from a measured zero, which is also not drawn
  // because an unchanged figure needs no arrow.
  if (value == null || value === 0) return undefined;
  const improving = lowerIsBetter ? value < 0 : value > 0;
  return {
    direction: improving ? ('up' as const) : ('down' as const),
    value: `${Math.abs(value).toFixed(1)}${unit}`,
    text: 'vs previous',
  };
}

export function KPIDashboard({ className }: KPIDashboardProps) {
  // Date range state - default to last 30 days
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });

  // Build query params
  const queryParams: KPIQueryParams = {
    startDate: dateRange?.from?.toISOString(),
    endDate: dateRange?.to?.toISOString(),
  };

  // Fetch KPI data with React Query
  const {
    data: kpis,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['shipment-kpis', queryParams],
    queryFn: () => tmsAnalyticsApi.getShipmentKPIs(queryParams),
    refetchInterval: 60000, // Auto-refresh every 60 seconds
    retry: 2,
  });

  // Show error toast if query fails
  if (isError) {
    toast.error('Failed to load KPI data', {
      description: error instanceof Error ? error.message : 'Unknown error',
    });
  }

  // Preset date ranges
  const presets = [
    {
      label: 'Last 7 days',
      value: () => ({
        from: subDays(new Date(), 7),
        to: new Date(),
      }),
    },
    {
      label: 'Last 30 days',
      value: () => ({
        from: subDays(new Date(), 30),
        to: new Date(),
      }),
    },
    {
      label: 'Last 90 days',
      value: () => ({
        from: subDays(new Date(), 90),
        to: new Date(),
      }),
    },
  ];

  return (
    <div className={className}>
      {/* Header with Date Range Selector */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">Performance Overview</h2>
          <p className="text-sm text-muted-foreground">
            {dateRange?.from && dateRange?.to
              ? `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`
              : 'Select date range'}
          </p>
        </div>

        {/* Date Range Picker */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              <Calendar className="mr-2 h-4 w-4" />
              Date Range
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <div className="flex">
              {/* Presets */}
              <div className="border-r p-2 space-y-1">
                {presets.map((preset) => (
                  <Button
                    key={preset.label}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-left font-normal"
                    onClick={() => setDateRange(preset.value())}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>

              {/* Calendar */}
              <div className="p-3">
                <CalendarComponent
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={2}
                  defaultMonth={dateRange?.from}
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* KPI Metric Cards */}
      <KPIStrip columns={4}>
        {/* 1. On-Time Delivery % */}
        <MetricCard
          label="On-Time Delivery"
          // Null is "nothing has resolved yet", which is not nought per cent.
          value={
            isLoading || !kpis
              ? '—'
              : kpis.onTimeDeliveryRate == null
                ? 'Not measured'
                : `${kpis.onTimeDeliveryRate.toFixed(1)}%`
          }
          subtitle={
            kpis
              ? kpis.onTimeDeliveryRate == null
                ? 'no shipment has resolved yet'
                : `${kpis.deliveredCount} of ${kpis.totalShipments} delivered`
              : undefined
          }
          icon={<TrendingUp className="h-5 w-5" />}
          trend={toTrend(kpis?.onTimeTrend, '%')}
          sentiment="success"
        />

        {/* 2. Avg Transit Time */}
        <MetricCard
          label="Avg Transit Time"
          value={
            isLoading || !kpis
              ? '—'
              : kpis.avgTransitDays == null
                ? 'Not measured'
                : `${kpis.avgTransitDays.toFixed(1)} days`
          }
          subtitle={
            kpis
              ? kpis.avgTransitDays == null
                ? 'no shipment has a recorded transit time'
                : `${kpis.inTransitCount} currently in transit`
              : undefined
          }
          icon={<Clock className="h-5 w-5" />}
          // Lower transit time is better, so the arrow direction is inverted.
          trend={toTrend(kpis?.transitTrend, ' days', true)}
          sentiment="info"
        />

        {/* 3. Active Exceptions */}
        <MetricCard
          label="Active Exceptions"
          value={isLoading ? '—' : kpis ? kpis.activeExceptions : '—'}
          subtitle={kpis ? `${kpis.delayedCount} shipments delayed` : undefined}
          icon={<AlertTriangle className="h-5 w-5" />}
          // Fewer exceptions is better.
          trend={toTrend(kpis?.exceptionTrend, '', true)}
          sentiment="warning"
        />

        {/* 4. Demurrage Charges */}
        <MetricCard
          label="Demurrage Charges"
          value={isLoading ? '—' : kpis ? `$${kpis.totalDemurrageCharges.toLocaleString()}` : '—'}
          subtitle={
            kpis && kpis.demurrageShipmentCount > 0
              ? `${kpis.demurrageShipmentCount} shipments affected`
              : 'No demurrage charges'
          }
          icon={<DollarSign className="h-5 w-5" />}
          sentiment="danger"
        />
      </KPIStrip>
    </div>
  );
}
